/**
 * OQ-291 · Danh sách sub-domain — mục "Sub-domain" trong nhóm Vận hành.
 *
 * **Chủ dự án**: *"tôi muốn thêm 1 mục ở vận hành, dưới domain là sub-domain."*
 *
 * ── Vì sao một mục riêng, không phải một tab trong chi tiết tên miền ───────
 *
 * Vì có người **chỉ thấy sub-domain**. Người được chia sẻ `blog.example.com`
 * không thấy `example.com` — nên nếu lối vào duy nhất nằm trong chi tiết tên
 * miền, họ không có đường nào tới thứ vừa được giao.
 *
 * Trang này là chỗ duy nhất họ vào được, nên nó phải đứng riêng ở thanh bên.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiWithMeta, thanhApiError, type ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { EmailLookupField, type EmailLookupState } from '../components/EmailLookupField';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { useFormat } from '../lib/format';
import { SearchBox } from '../components/SearchBox';
import { Pagination } from '../components/Pagination';
import { SortButton } from '../components/SortButton';
import { ShareCountCell } from '../components/ShareCountCell';

export interface SubdomainItem {
  id: string;
  domainId: string;
  domainName: string;
  label: string;
  fqdn: string;
  recordCount: number;
  pendingCount: number;
  shareCount: number;
  relation: 'domain' | 'granted';
  createdAt: string;
}

interface DomainOption {
  id: string;
  name: string;
  capabilities?: string[];
}

export function SubdomainsPage(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  /* Quản trị viên thấy mọi sub-domain nhưng thường không có grant trên tên miền
     của người khác — xem chú thích ở nút xoá. Cùng biểu thức với DomainsPage. */
  const isAdmin = can('dom.viewall');
  const { formatDateTime, formatNumber } = useFormat();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [tim, setTim] = useState('');
  const [trang, setTrang] = useState(1);
  const [soDong, setSoDong] = useState(25);
  const [sort, setSort] = useState('fqdn:asc');
  const [moTao, setMoTao] = useState(false);
  const [xoa, setXoa] = useState<SubdomainItem | null>(null);

  const danhSach = useQuery({
    queryKey: ['subdomains', { tim, trang, soDong, sort }],
    queryFn: () => {
      const q = new URLSearchParams({ page: String(trang), perPage: String(soDong) });
      if (tim) q.set('q', tim);
      q.set('sort', sort);
      return apiWithMeta<SubdomainItem[]>(`/subdomains?${q.toString()}`);
    },
  });

  const items = danhSach.data?.data ?? [];
  const tong = danhSach.data?.meta?.total ?? 0;

  return (
    <div data-od-id="page-subdomains">
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="subdomains-title">{t('subdomains.title')}</h1>
          <p className="lede">{t('subdomains.lede')}</p>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => setMoTao(true)}
          data-od-id="subdomain-add"
        >
          <Icon name="plus" size={16} />
          {t('subdomains.add')}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <SearchBox
            value={tim}
            onChange={(v) => {
              setTim(v);
              setTrang(1);
            }}
            placeholder={t('subdomains.searchPlaceholder')}
            odId="subdomain-search"
          />
        </div>

        <AsyncSection
          loading={danhSach.isLoading}
          error={danhSach.error}
          data={items}
          onRetry={() => void danhSach.refetch()}
          skeleton={<SkeletonCard rows={5} />}
          empty={
            <EmptyState
              icon="domains"
              title={t('subdomains.emptyTitle')}
              body={t('subdomains.emptyBody')}
            />
          }
        >
          {(ds) => (
            <div className="table-wrap">
              <table className="tbl" data-od-id="subdomains-table">
                <thead>
                  <tr>
                    <th>
                      <SortButton
                        label={t('subdomains.col.fqdn')}
                        field="fqdn"
                        sort={sort}
                        onSort={setSort}
                        odId="subdomains-sort-fqdn"
                      />
                    </th>
                    <th>
                      <SortButton
                        label={t('subdomains.col.domain')}
                        field="domain"
                        sort={sort}
                        onSort={setSort}
                        odId="subdomains-sort-domain"
                      />
                    </th>
                    <th className="right">
                      <SortButton
                        label={t('subdomains.col.records')}
                        field="records"
                        sort={sort}
                        onSort={setSort}
                        odId="subdomains-sort-records"
                      />
                    </th>
                    <th className="right">
                      <SortButton
                        label={t('subdomains.col.shares')}
                        field="shares"
                        sort={sort}
                        onSort={setSort}
                        odId="subdomains-sort-shares"
                      />
                    </th>
                    <th>{t('subdomains.col.relation')}</th>
                    <th>
                      <SortButton
                        label={t('subdomains.col.created')}
                        field="createdAt"
                        sort={sort}
                        onSort={setSort}
                        odId="subdomains-sort-created"
                      />
                    </th>
                    {/* Cột hành động phải có nhãn như năm trang còn lại — một
                        `<th>` rỗng khiến trình đọc màn hình đọc ra khoảng trắng,
                        và mắt người thì không biết cột ấy để làm gì. */}
                    <th className="right">{t('subdomains.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="cell-main">
                          <b>
                            <Link
                              to={`/subdomains/${s.id}`}
                              style={{ textDecoration: 'none' }}
                              data-od-id={`subdomain-open-${s.label}`}
                            >
                              {s.fqdn}
                            </Link>
                          </b>
                        </div>
                        <div className="sub mono">{s.label}</div>
                      </td>
                      <td className="muted">{s.domainName}</td>
                      <td className="right num">{formatNumber(s.recordCount)}</td>
                      <td className="right num">
                        <ShareCountCell
                          count={s.shareCount}
                          pending={s.pendingCount}
                          to={`/subdomains/${s.id}?tab=sharing`}
                          odId={`subdomain-shares-${s.label}`}
                        />
                      </td>
                      <td>
                        <span className={`chip ${s.relation === 'domain' ? '' : 'accent'}`}>
                          {t(`subdomains.relation.${s.relation}`)}
                        </span>
                      </td>
                      <td className="small muted">{formatDateTime(s.createdAt)}</td>
                      <td className="right">
                        <div className="row-actions">
                          {/*
                            Ai xoá được — OQ-312.

                            `relation === 'domain'` nghĩa là người này có grant
                            trên tên miền cha. Nhưng **quản trị viên** thấy mọi
                            sub-domain qua `dom.viewall` mà thường KHÔNG có grant
                            trên tên miền của người khác — nên với họ `relation`
                            là `'granted'`, và nút biến mất ở đúng những dòng họ
                            cần thao tác nhất.
                            
                            `DomainsPage` đã giải đúng từ M3: `relation === 'owned'
                            || isAdmin`. Tôi chép hình dạng bảng của nó ở OQ-311
                            mà không chép điều kiện này.
                          */}
                          {s.relation === 'domain' || isAdmin ? (
                            <button
                              className="btn btn-sm btn-danger"
                              type="button"
                              aria-label={t('subdomains.delete.action')}
                              title={t('subdomains.delete.action')}
                              onClick={() => setXoa(s)}
                              data-od-id={`subdomain-delete-${s.label}`}
                            >
                              <Icon name="trash" size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncSection>

        <div className="card-foot">
          {/*
            Thanh phân trang phải nằm trong `.card-foot` — nó mang `padding` và
            `border-top`. Đặt trần trong `.card` thì cụm nút dính sát mép và
            không có đường kẻ ngăn với bảng, lệch hẳn với năm trang còn lại.
          */}
          <span className="small muted num">
            {t('subdomains.count', { n: formatNumber(tong) })}
          </span>
          <span style={{ flex: 1 }} />
          <Pagination
            page={trang}
            perPage={soDong}
            total={tong}
            onPage={setTrang}
            onPerPage={(n) => {
              setSoDong(n);
              setTrang(1);
            }}
            odId="subdomains-pagination"
          />
        </div>
      </div>

      {moTao ? (
        <TaoSubdomainModal
          onClose={() => setMoTao(false)}
          onDone={() => {
            setMoTao(false);
            void queryClient.invalidateQueries({ queryKey: ['subdomains'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
            toast(t('subdomains.created'));
          }}
        />
      ) : null}

      {xoa ? (
        <XoaSubdomainModal
          sub={xoa}
          onClose={() => setXoa(null)}
          onDone={(soBanGhi) => {
            setXoa(null);
            void queryClient.invalidateQueries({ queryKey: ['subdomains'] });
            /* Bộ đếm sidebar đọc từ `dashboard/summary` — không làm mới thì con
               số đứng yên và nói dối cho tới lần tải trang sau. */
            void queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
            toast(
              soBanGhi > 0
                ? t('subdomains.delete.doneWithRecords', { n: soBanGhi })
                : t('subdomains.delete.done'),
            );
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Xoá sub-domain — hai lựa chọn, mặc định là lựa chọn AN TOÀN (OQ-310).
 *
 * **Chủ dự án**: *"Khi xóa thì sẽ hiện lên hộp thoại với 2 lựa chọn, 1 là xóa và
 * giữ nguyên bản ghi dns và 2 là xóa tất cả bản ghi của sub-domain đó"*.
 *
 * Hai lựa chọn khác nhau một trời một vực:
 *
 *   · **Giữ bản ghi** — gỡ một đơn vị uỷ quyền. Website vẫn chạy.
 *   · **Xoá cả bản ghi** — hạ `blog.example.com` khỏi Internet, ngay lập tức,
 *     trên Cloudflare thật, không lùi được.
 *
 * Nên lựa chọn thứ hai bắt **gõ lại FQDN**. Cùng khuôn với xoá tên miền và xoá
 * kết nối: thao tác không lùi được thì phải có một bước mà phản xạ không vượt
 * qua nổi.
 *
 * Export để `oq-313` render thật được — chốt đọc mã nguồn không phân biệt nổi
 * "mã có viết" với "hệ thống có chạy" (bài học OQ-312).
 */
export function XoaSubdomainModal({
  sub,
  onClose,
  onDone,
}: {
  sub: SubdomainItem;
  onClose: () => void;
  onDone: (soBanGhi: number) => void;
}): JSX.Element {
  const t = useT();
  const [xoaBanGhi, setXoaBanGhi] = useState(false);
  const [goLai, setGoLai] = useState('');
  const [loi, setLoi] = useState<ApiError | null>(null);

  const xoa = useMutation({
    mutationFn: () =>
      api.del<{ deletedRecords: number }>(
        `/subdomains/${sub.id}${xoaBanGhi ? '?deleteRecords=true' : ''}`,
      ),
    onSuccess: (kq) => onDone(kq?.deletedRecords ?? 0),
    onError: (e: unknown) => setLoi(thanhApiError(e)),
  });

  /* Chỉ nhánh nguy hiểm mới đòi gõ lại tên. Bắt gõ cho cả nhánh an toàn thì
     người ta học cách gõ nhanh, và bước ấy hết tác dụng ở đúng lúc cần. */
  const sanSang = !xoaBanGhi || goLai.trim() === sub.fqdn;

  return (
    <Modal open title={t('subdomains.delete.title', { fqdn: sub.fqdn })} onClose={onClose}>
      <ApiErrorAlert error={loi} />

      <div className="stack" style={{ gap: 12 }}>
        <label className="choice-row">
          <input
            type="radio"
            name="sub-delete-mode"
            checked={!xoaBanGhi}
            onChange={() => {
              setXoaBanGhi(false);
              setGoLai('');
            }}
            data-od-id="subdomain-delete-keep"
          />
          <span>
            <b>{t('subdomains.delete.keepTitle')}</b>
            <span className="sub">{t('subdomains.delete.keepSub')}</span>
          </span>
        </label>

        <label className="choice-row">
          <input
            type="radio"
            name="sub-delete-mode"
            checked={xoaBanGhi}
            onChange={() => setXoaBanGhi(true)}
            data-od-id="subdomain-delete-purge"
          />
          <span>
            <b>{t('subdomains.delete.purgeTitle', { n: sub.recordCount })}</b>
            <span className="sub">{t('subdomains.delete.purgeSub')}</span>
          </span>
        </label>
      </div>

      {/* Quyền chia sẻ mất theo trong CẢ HAI lựa chọn — nói ra để người bấm khỏi
          tưởng nhánh "giữ bản ghi" cũng giữ luôn quyền. */}
      <div className="form-alert" role="note" style={{ marginTop: 12 }}>
        <Icon name="info" size={16} />
        <span>{t('subdomains.delete.grantsNote')}</span>
      </div>

      {/* Nửa sự thật còn lại: lời mời QUYỀN mất theo, nhưng thư mời TÀI KHOẢN
          vẫn dùng được — người kia vẫn tạo được tài khoản rồi vào một chỗ
          trống. Đo ở OQ-313. Chỉ hiện khi thật sự có người đang chờ, và con số
          đi cùng danh sách nên nó lấy từ cùng một điều kiện lọc.

          Cảnh báo này nằm sẵn trong lần vẽ đầu, không đợi một lượt tải riêng:
          cảnh báo đến sau khi người ta đã bấm thì không còn là cảnh báo. */}
      {sub.pendingCount > 0 ? (
        <div className="form-alert warn" role="alert" style={{ marginTop: 8 }}>
          <Icon name="warn" size={16} />
          <span>{t('subdomains.delete.pendingWarn', { n: sub.pendingCount })}</span>
        </div>
      ) : null}

      {xoaBanGhi ? (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          <div className="form-alert bad" role="alert">
            <Icon name="warn" size={16} />
            <span>{t('subdomains.delete.purgeWarn', { fqdn: sub.fqdn })}</span>
          </div>
          <Field
            label={t('subdomains.delete.confirmLabel', { fqdn: sub.fqdn })}
            value={goLai}
            onChange={(e) => setGoLai(e.target.value)}
            placeholder={sub.fqdn}
            data-od-id="subdomain-delete-confirm"
          />
        </div>
      ) : null}

      <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn" type="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-danger"
          type="button"
          onClick={() => xoa.mutate()}
          disabled={xoa.isPending || !sanSang}
          data-od-id="subdomain-delete-submit"
        >
          {xoa.isPending ? t('common.processing') : t('subdomains.delete.action')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Hộp thoại tạo sub-domain.
 *
 * Export để `oq-312-*.test.tsx` render thật và hỏi thẳng cái nút — chốt đọc mã
 * nguồn không phân biệt được "mã có viết" với "hệ thống có chạy".
 */
export function TaoSubdomainModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const t = useT();
  const { toast } = useToast();
  const [domainId, setDomainId] = useState('');
  const [label, setLabel] = useState('');
  const [tra, setTra] = useState<EmailLookupState | null>(null);
  const [loi, setLoi] = useState<ApiError | null>(null);

  /*
   * Chỉ liệt kê tên miền mà người dùng THỰC SỰ tạo được sub trên đó.
   *
   * Máy chủ trả `capabilities` cho từng tên miền, nên lọc ở đây đọc cùng một
   * nguồn sự thật với cổng quyền — không phải một bản sao luật ở giao diện.
   * Để cả danh sách thì người dùng chọn xong mới nhận 403, và không có cách
   * nào biết trước tên miền nào dùng được.
   */
  const tenMien = useQuery({
    queryKey: ['domains', 'for-subdomain'],
    queryFn: () => api.get<{ domains: DomainOption[] }>('/domains?perPage=200'),
  });

  const chonDuoc = useMemo(
    () =>
      (tenMien.data?.domains ?? []).filter((d) => (d.capabilities ?? []).includes('sub.create')),
    [tenMien.data],
  );

  /**
   * Tra cứu tại chỗ, KHÔNG hộp thoại thứ hai — OQ-308.
   *
   * **Chủ dự án**: *"tôi muốn chức năng cấp quyền hoạt động tương tự như thêm
   * người dùng cho tên miền gốc ấy, hiện tại đang hoạt động mà không hỏi gì"*.
   *
   * OQ-307 giải đúng vấn đề (người bấm Tạo cần biết mình sắp cấp quyền cho ai)
   * nhưng giải bằng một hộp thoại chặn đường — trong khi hệ thống đã có sẵn lối
   * trả lời cùng câu hỏi ấy ngay trong biểu mẫu.
   *
   * > Hộp xác nhận trả lời câu hỏi **sau khi** người ta đã quyết. Ô tra cứu trả
   * > lời nó **trong lúc** họ đang quyết, và không ai phải bấm thêm nút nào.
   */
  const tao = useMutation({
    mutationFn: () =>
      api.post<{ share?: { mode: 'granted' | 'invited' } }>(`/domains/${domainId}/subdomains`, {
        label,
        /*
         * OQ-306 — chỉ gửi khoá `shareEmail` khi có nhập.
         *
         * Gửi chuỗi rỗng thì schema `.email()` ở máy chủ từ chối, và người dùng
         * nhận lỗi cho một ô họ cố tình bỏ trống.
         */
        ...(tra?.email ? { shareEmail: tra.email } : {}),
      }),
    onSuccess: (kq) => {
      if (kq.share) {
        toast(
          t(kq.share.mode === 'invited' ? 'subdomains.share.invited' : 'subdomains.share.granted'),
        );
      }
      onDone();
    },
    onError: (e: unknown) => setLoi(thanhApiError(e)),
  });

  const goc = chonDuoc.find((d) => d.id === domainId)?.name;

  /**
   * Có gõ email thì PHẢI tra cứu xong mới cho tạo — OQ-311.
   *
   * **Chủ dự án**: *"Khi tôi nhập email để chia sẻ khi tạo sub-domain mà tôi
   * không bấm thêm thì vẫn bấm nút tạo được. phải bấm thêm để kiểm tra email
   * trước chứ"*.
   *
   * OQ-308 để nút Tạo luôn bật với lý do *"email chỉ là tuỳ chọn"*. Lý do ấy chỉ
   * đúng khi ô trống. Khi đã gõ vào đó, người dùng **đang chia sẻ** — và họ bấm
   * Tạo mà chưa biết email ấy có tài khoản hay không, có gõ nhầm hay không.
   *
   * > Nút bật nghĩa là *"làm được"*. Để nó bật giữa lúc một nửa việc chưa xác
   * > minh là hứa một điều mình chưa kiểm.
   *
   * Ô trống → bật (tạo nhánh trống là việc hợp lệ). Ô có chữ → phải tra xong.
   */
  const coEmail = tra?.coChu === true;
  const emailSanSang = !coEmail || Boolean(tra && (tra.found || tra.missing) && !tra.looking);

  return (
    <Modal open title={t('subdomains.addTitle')} onClose={onClose}>
      <form
        className="stack"
        data-od-id="subdomain-add-form"
        onSubmit={(e) => {
          e.preventDefault();
          setLoi(null);
          tao.mutate();
        }}
      >
        <ApiErrorAlert error={loi} />

        <label className="field">
          <span className="label">{t('subdomains.field.domain')}</span>
          <select
            value={domainId}
            onChange={(e) => setDomainId(e.target.value)}
            required
            data-od-id="subdomain-domain"
          >
            <option value="">{t('subdomains.field.domainPlaceholder')}</option>
            {chonDuoc.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          {tenMien.isSuccess && chonDuoc.length === 0 ? (
            <span className="hint">{t('subdomains.noDomainCanCreate')}</span>
          ) : null}
        </label>

        <Field
          label={t('subdomains.field.label')}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="blog"
          required
          data-od-id="subdomain-label"
          hint={
            goc
              ? t('subdomains.field.labelHintWith', { fqdn: `${label || '…'}.${goc}` })
              : t('subdomains.field.labelHint')
          }
        />

        {/*
          OQ-306/308 — chia sẻ ngay lúc tạo, tuỳ chọn, tra cứu TẠI CHỖ.

          Chủ dự án: *"Tạo subdomain thì có luôn ô nhập email người dùng muốn
          chia sẻ […] không nhập thì tạo bình thường"*, rồi sau đó: *"hoạt động
          tương tự như thêm người dùng cho tên miền gốc ấy, hiện tại đang hoạt
          động mà không hỏi gì"*.

          Nút tra cứu ở đây là TUỲ CHỌN, khác hộp thoại Chia sẻ — vì email cũng
          tuỳ chọn. Bắt tra cứu mới cho tạo nghĩa là chặn cả người chỉ muốn tạo
          một nhánh trống.
        */}
        <EmailLookupField
          idPrefix="subdomain-share"
          label={t('subdomains.field.shareEmail')}
          hintIdle={t('subdomains.field.shareEmailHint')}
          onChange={setTra}
        />

        <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={tao.isPending || !domainId || !label || !emailSanSang}
            data-od-id="subdomain-add-submit"
          >
            {tao.isPending ? t('common.processing') : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
